import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useOmniContractRemoveScopeOfWorkMutation } from "../api/contract-api";

/**
 * ContractScopeOfWorkDeleteDialog removes the scope of work scopeOfWorkId
 * with its positions from the contract contractId after a confirmation.
 */
export function ContractScopeOfWorkDeleteDialog({
  contractId,
  scopeOfWorkId,
  onDeleted,
  disabled,
}: {
  contractId: string;
  scopeOfWorkId?: string;
  onDeleted?: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [remove, { isLoading, error, reset }] =
    useOmniContractRemoveScopeOfWorkMutation();

  const onConfirm = () => {
    if (isLoading || !scopeOfWorkId) {
      return;
    }
    remove({ id: contractId, scopeOfWorkId })
      .unwrap()
      .then(() => {
        toast.success(t("Leistung gelöscht"));
        reset();
        setOpen(false);
        onDeleted?.();
      })
      .catch(() => {
        // The error is shown by the RTKQueryErrorAlert.
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Trash2}
        label=""
        variant="destructive"
        disabled={disabled || !scopeOfWorkId}
        aria-label={t("Leistung löschen")}
      />
      <DialogContent>
        <CommonDialogHeader
          title={t("Leistung wirklich löschen?")}
          description={t(
            "Die Leistung wird mit allen Positionen gelöscht. Dies kann nicht rückgängig gemacht werden.",
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
